import { getJoin, InvalidInput, join, type User } from '@trippath/shared';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { problemKey } from '../../lib/format';
import { Field } from './SignIn';

/** An account made by an invitation from whoever runs the server (`/join/{token}`). */
export function Join({ onSignedIn }: { onSignedIn: (user: User) => void }) {
  const { t, i18n } = useTranslation();
  const ids = useId();
  const navigate = useNavigate();
  const { token = '' } = useParams();
  const [invited, setInvited] = useState<
    { kind: 'asking' } | { kind: 'gone' } | { kind: 'open'; email: string }
  >({ kind: 'asking' });
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    getJoin(token).then(
      (email) => {
        setInvited({ kind: 'open', email });
      },
      () => {
        setInvited({ kind: 'gone' });
      },
    );
  }, [token]);

  if (invited.kind === 'asking') return <p className="page quiet">{t('trips.loading')}</p>;
  if (invited.kind === 'gone') {
    return (
      <div className="page sign-in">
        <p className="panel form-failed" role="alert">
          {t('join.gone')}
        </p>
      </div>
    );
  }
  return (
    <div className="page sign-in">
      <form
        className="panel form"
        noValidate
        aria-labelledby={`${ids}-title`}
        onSubmit={(event) => {
          event.preventDefault();
          setErrors({});
          setFailed(false);
          join(token, { name, password, language: i18n.language }).then(
            (user) => {
              onSignedIn(user);
              void navigate('/');
            },
            (problem: unknown) => {
              if (problem instanceof InvalidInput) {
                setErrors(
                  Object.fromEntries(
                    problem.fields.map((field) => [field.field, t(problemKey(field.problem))]),
                  ),
                );
              } else {
                setFailed(true);
              }
            },
          );
        }}
      >
        <h1 id={`${ids}-title`}>{t('join.title')}</h1>
        <p className="quiet">{t('join.lead', { email: invited.email })}</p>
        <Field
          id={`${ids}-name`}
          label={t('account.name')}
          type="text"
          autoComplete="name"
          value={name}
          error={errors.name}
          onChange={setName}
        />
        <Field
          id={`${ids}-password`}
          label={t('account.password')}
          type="password"
          autoComplete="new-password"
          value={password}
          error={errors.password}
          onChange={setPassword}
        />
        <p className="quiet">{t('account.passwordHint')}</p>
        {failed && (
          <p className="form-failed" role="alert">
            {t('trips.failed')}
          </p>
        )}
        <button type="submit" className="button button-primary">
          {t('join.create')}
        </button>
      </form>
    </div>
  );
}
